import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ProfileCard from '../components/ProfileCard';
import { KATEGORIER } from '../components/CategorySelector';
import { getFylkeName, getByName } from '../data/norwegianCities';
import { isDemoMode } from '../demo/useDemoMode';
import { getJobById, getUserById, getReviewsForUser } from '../demo/mockData';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const JobDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContact, setShowContact] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Check if we came from chat
  const fromChat = location.state?.fromChat || false;

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJob = async () => {
    // In demo mode, use mock data
    if (isDemoMode()) {
      const mockJob = getJobById(parseInt(id));
      if (mockJob) {
        // Get poster details and reviews
        const poster = getUserById(mockJob.poster?.id);
        const reviews = getReviewsForUser(mockJob.poster?.id);
        
        setJob({
          ...mockJob,
          poster: {
            ...mockJob.poster,
            ...poster,
            email: poster?.email || 'demo@minitask.no',
            phone: poster?.phone || '12345678',
            reviews: reviews
          }
        });
      }
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      console.error('Feil ved henting av annonse:', error);
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!isAuthenticated) {
      navigate('/logg-inn');
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API_URL}/payments/vipps/init`, {
        job_id: job.id,
        amount: job.price
      });
      
      // Redirect til Vipps betalingsside
      if (response.data.redirectUrl) {
        window.location.href = response.data.redirectUrl;
      }
    } catch (error) {
      console.error('Feil ved initiering av betaling:', error);
      alert('Kunne ikke starte betaling. Prøv igjen senere.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const kategori = job ? KATEGORIER.find(k => k.id === job.category) : null;
  const fylkeNavn = job ? getFylkeName(job.location_fylke) : null;
  const byNavn = job?.location_city ? getByName(job.location_city) : null;

  const handleSendMessage = () => {
    if (!isAuthenticated) {
      navigate('/logg-inn', { state: { from: `/annonse/${id}` } });
      return;
    }
    navigate(`/meldinger?job=${job.id}&user=${job.poster?.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-64 bg-slate-200 rounded-xl"></div>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <div className="h-8 bg-slate-200 rounded w-3/4"></div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
            work_off
          </span>
          <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
            Annonse ikke funnet
          </h2>
          <Link to="/" className="text-[#1E3A5F] hover:underline">
            Gå tilbake til forsiden
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Tilbake-knapp */}
        <button
          onClick={() => fromChat ? navigate('/meldinger') : navigate('/')}
          className="inline-flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] mb-6 transition-colors"
        >
          <span className="material-symbols-outlined">arrow_back</span>
          {fromChat ? 'Tilbake til chat' : 'Tilbake til annonser'}
        </button>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Hovedinnhold */}
          <div className="md:col-span-2 space-y-6">
            {/* Bilder */}
            {job.images && job.images.length > 0 ? (
              <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                <img 
                  src={job.images[0].startsWith('http') ? job.images[0] : `${API_URL.replace('/api', '')}${job.images[0]}`} 
                  alt={job.title}
                  className="w-full aspect-video object-cover"
                />
                {job.images.length > 1 && (
                  <div className="p-4 flex gap-2 overflow-x-auto">
                    {job.images.slice(1).map((img, i) => (
                      <img 
                        key={i}
                        src={img.startsWith('http') ? img : `${API_URL.replace('/api', '')}${img}`}
                        alt=""
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] rounded-xl aspect-video flex items-center justify-center">
                <span className="material-symbols-outlined text-white/20 text-8xl">
                  {kategori?.icon || 'work'}
                </span>
              </div>
            )}

            {/* Annonsedetaljer */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              {/* Header */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {/* Job type badge */}
                {job.job_type && (
                  <span className={`inline-flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full ${
                    job.job_type === 'tjeneste' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    <span className="material-symbols-outlined text-base">
                      {job.job_type === 'tjeneste' ? 'handyman' : 'assignment'}
                    </span>
                    {job.job_type === 'tjeneste' ? 'Tjeneste' : 'Oppdrag'}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 bg-[#0A1929]/5 text-[#0A1929] text-sm font-medium px-3 py-1 rounded-full">
                  <span className="material-symbols-outlined text-base">{kategori?.icon || 'work'}</span>
                  {kategori?.name || job.category}
                </span>
                <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-sm px-3 py-1 rounded-full">
                  <span className="material-symbols-outlined text-base">location_on</span>
                  {byNavn ? `${byNavn}, ${fylkeNavn}` : fylkeNavn || job.location_fylke}
                </span>
                {job.status === 'active' && (
                  <span className="bg-green-100 text-green-700 text-sm px-3 py-1 rounded-full">
                    Aktiv
                  </span>
                )}
              </div>

              <h1 className="font-heading font-bold text-2xl text-[#0A1929] mb-4">
                {job.title}
              </h1>

              <div className="prose prose-slate max-w-none mb-6">
                <p className="whitespace-pre-wrap text-slate-600">{job.description}</p>
              </div>

              {/* Pris */}
              <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                <span className="text-slate-600">
                  {job.job_type === 'tjeneste' ? 'Pris for tjenesten:' : 'Betaling for oppdraget:'}
                </span>
                <span className="font-heading font-bold text-2xl text-[#0A1929]">
                  {job.pricing_type === 'hourly' 
                    ? `${(job.hourly_rate || 0).toLocaleString('nb-NO')} kr/time`
                    : job.pricing_type === 'negotiable'
                      ? 'Etter avtale'
                      : `${(job.price || 0).toLocaleString('nb-NO')} kr`
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1 space-y-6">
            {/* Oppdragsgiver */}
            <div>
              <h3 className="font-heading font-semibold text-[#0A1929] mb-3">Oppdragsgiver</h3>
              {job.poster && (
                <ProfileCard user={{
                  ...job.poster,
                  profile_image: job.poster.profile_image?.startsWith('http') 
                    ? job.poster.profile_image 
                    : job.poster.profile_image 
                      ? `${API_URL.replace('/api', '')}${job.poster.profile_image}`
                      : null
                }} />
              )}
            </div>

            {/* Handlinger */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
              {/* Send melding - hovedknapp */}
              <button
                onClick={handleSendMessage}
                className="w-full bg-[#0A1929] text-white py-3 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined">chat</span>
                Send melding
              </button>

              {/* Kontaktinfo - krever innlogging */}
              {!isAuthenticated ? (
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <span className="material-symbols-outlined text-slate-400 text-2xl mb-2 block">lock</span>
                  <p className="text-sm text-slate-500 mb-3">
                    Logg inn for å se kontaktinformasjon
                  </p>
                  <Link
                    to="/logg-inn"
                    state={{ from: `/annonse/${id}` }}
                    className="text-[#1E3A5F] font-medium hover:underline text-sm"
                  >
                    Logg inn eller registrer deg
                  </Link>
                </div>
              ) : !showContact ? (
                <button
                  onClick={() => setShowContact(true)}
                  className="w-full bg-slate-100 text-[#0A1929] py-3 rounded-lg font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined">contact_phone</span>
                  Vis kontaktinfo
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <span className="material-symbols-outlined text-[#1E3A5F]">email</span>
                    <a href={`mailto:${job.poster?.email}`} className="text-[#1E3A5F] hover:underline">
                      {job.poster?.email}
                    </a>
                  </div>
                  {job.poster?.phone && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <span className="material-symbols-outlined text-[#1E3A5F]">phone</span>
                      <a href={`tel:${job.poster.phone}`} className="text-[#1E3A5F] hover:underline">
                        {job.poster.phone}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Vipps betaling - kun for tjenester når innlogget */}
              {isAuthenticated && job.job_type === 'tjeneste' && job.pricing_type !== 'negotiable' && (
                <button
                  onClick={handlePayment}
                  disabled={paymentLoading}
                  className="w-full bg-[#ff5b24] text-white py-3 rounded-lg font-medium hover:bg-[#e54e1a] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {paymentLoading ? (
                    <>
                      <span className="animate-spin material-symbols-outlined">progress_activity</span>
                      Starter...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">payments</span>
                      Betal og bestill
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-4">
              <h4 className="font-medium text-[#0A1929] mb-2 flex items-center gap-2">
                <span className="material-symbols-outlined text-blue-500">tips_and_updates</span>
                Tips
              </h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Sjekk alltid tilbakemeldinger før du tar oppdraget</li>
                <li>• Avtal detaljer skriftlig</li>
                <li>• Bruk Vipps for trygg betaling</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsPage;
